import { uploadUserFileToGcs, useApiClient } from '@eluve/api-client-provider';
import { UserFileTypesLookup } from '@eluve/graphql-types';
import { useAnalytics } from '@eluve/posthog-react';

import { useSignedUrl } from '../useSignedUrl';

import type { PdfGenerateResult } from './PdfTemplateCreateFlow';

const errorMessage = (body: unknown, fallback: string): string =>
  (body as { error?: string })?.error ?? fallback;

export const usePdfTemplateOperations = (tenantId: string | null) => {
  const apiClient = useApiClient();
  const { capture } = useAnalytics();

  const requireTenantId = (): string => {
    if (!tenantId) throw new Error('Unable to determine tenant');
    return tenantId;
  };

  const uploadFile = (file: File): Promise<string> =>
    uploadUserFileToGcs({
      apiClient,
      tenantId: requireTenantId(),
      file,
      fileType: UserFileTypesLookup.PDF_FORM_TEMPLATE,
    });

  const generate = async (userUploadId: string): Promise<PdfGenerateResult> => {
    const r = await apiClient.documents.generatePdfFormTemplateFields({
      params: { tenantId: requireTenantId() },
      body: { userUploadId },
    });
    if (r.status !== 200) {
      throw new Error(errorMessage(r.body, 'Could not process PDF form.'));
    }
    const { generatedFileId, fields, alreadyFillable } = r.body;
    return { generatedFileId, fields, alreadyFillable };
  };

  const save = async (
    sourceFileId: string,
    name: string,
  ): Promise<{ templateId: string; name: string }> => {
    const r = await apiClient.documents.createPdfFormTemplate({
      params: { tenantId: requireTenantId() },
      body: { sourceFileId, name },
    });
    if (r.status !== 200) {
      throw new Error(errorMessage(r.body, 'Could not save template.'));
    }
    capture('pdf_form_template_created', { fieldCount: r.body.fieldCount });
    return r.body;
  };

  const usePreviewUrl = (fileId: string | null): string | null =>
    useSignedUrl(fileId).url ?? null;

  return { uploadFile, generate, save, usePreviewUrl };
};
